
import React from "react";
import { Link } from "react-router-dom";
import { Calendar } from "lucide-react";
import { BlogPost } from "@/types/blog";

interface BlogCardProps {
  post: BlogPost;
}

const BlogCard = ({ post }: BlogCardProps) => {
  // Default image for posts without one
  const imageUrl = post.imageUrl || "/images/blog/blog-default.jpg";
  
  // Format date for schema.org if it's not "In Vorbereitung"
  let formattedDate = null;
  if (post.date !== "In Vorbereitung") {
    try {
      // Split by dots and reverse for ISO format (DD.MM.YYYY -> YYYY-MM-DD)
      const parts = post.date.split('.');
      if (parts.length === 3) {
        const day = parts[0].trim();
        const month = parts[1].trim();
        const year = parts[2].trim();
        formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    } catch (error) {
      console.error("Error formatting date:", error);
      formattedDate = null;
    }
  }
    
  return (
    <div className="flex flex-col h-full rounded-2xl overflow-hidden shadow-soft group bg-white transition-all hover:translate-y-[-4px] hover:shadow-md" itemScope itemType="https://schema.org/BlogPosting">
      {formattedDate && <meta itemProp="datePublished" content={formattedDate} />}
      <meta itemProp="author" content="Jörg Hörmann" />
      
      {/* Image section with improved aspect ratio */}
      <div className="relative h-52 overflow-hidden">
        <img 
          src={imageUrl} 
          alt={post.title} 
          itemProp="image"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          width="400"
          height="250"
          decoding="async"
        />
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 bg-primary/90 text-white text-xs font-medium rounded-full" itemProp="articleSection">
            {post.category}
          </span>
        </div>
      </div>

      {/* Content section with improved whitespace */}
      <div className="p-6 flex flex-col flex-grow">
        <div className="flex items-center gap-2 text-sm text-joerg-500 mb-2">
          <Calendar className="h-4 w-4" />
          <span className={post.comingSoon ? "italic" : ""}>
            {post.date}
          </span>
        </div>
        <h3 className="text-xl font-semibold mb-3 line-clamp-2 group-hover:text-primary transition-colors" itemProp="headline">
          {post.title}
        </h3>
        <p className="text-joerg-600 text-sm flex-grow line-clamp-3 mb-4" itemProp="description">
          {post.excerpt}
        </p>

        {/* Conditional CTA based on post status */}
        {post.comingSoon ? (
          <div className="mt-auto pt-2">
            <span className="text-joerg-500 text-sm italic">
              Artikel in Vorbereitung
            </span>
          </div>
        ) : (
          <div className="mt-auto pt-2">
            <Link 
              to={`/blog/${post.slug}`} 
              className="text-primary font-medium group-hover:underline inline-block"
              itemProp="url"
              aria-label={`Artikel lesen: ${post.title}`}
            >
              Artikel lesen
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogCard;
